import { useMediaQuery } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';
import { LeftPanelSectionTemplate } from '../../../components/AdminTemplates';
import OutletContextReliantRoute from '../../../components/OutletContextReliantRoute';
import { SideActionItem } from '../../../components/SideActionsList';
import VesselOrCrewStatusBadge from '../../../components/VesselOrCrewStatusBadge';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { disableNavigationGuard } from '../../../hooks/useNavigationGuard';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { CrewMember } from '../../../types/crew';
import { Role } from '../../../types/user';
import { getIconCertificateStatus } from '../../../utils/certificates';
import { StatusIcon } from '../../../utils/icon';
import CrewDeletionDialog from './CrewDeletionDialog';
import CrewTemplateActions from './CrewTemplateActions';
import { CrewTemplateContext } from './types';
import {
  CrewMemberCertificateStatus,
  CrewMemberRole,
} from '../../../types/core';

const CrewTemplate = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { crewMemberId: crewMemberIdQueryParam } = useParams();
  const {
    getCrewMemberById: getCrewById,
    getCrewMemberCertificates: getCrewCertificates,
  } = useIotsApi();
  const [displayedCrewName, setDisplayedCrewName] = useState<string>('');
  const { hasRole } = useCurrentUser();
  const isAdmin = hasRole(Role.ADMIN);
  const isMdUp = useMediaQuery('(min-width:768px)');

  const crewMemberId = useMemo(() => {
    return typeof crewMemberIdQueryParam !== 'string' ||
      crewMemberIdQueryParam.toLocaleLowerCase() === 'create'
      ? null
      : crewMemberIdQueryParam;
  }, [crewMemberIdQueryParam]);

  const {
    data: crewMember = null,
    isFetching: isFetchingCrewMember,
    error,
    refetch,
  } = useQuery({
    queryKey: ['crew', crewMemberId ?? ''],
    queryFn: () => getCrewById(crewMemberId ?? ''),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: crewMemberId != null,
  });

  const {
    data: crewMemberCertificates = [],
    isFetching: isFetchingCertificates,
    error: errorCertficates,
    isLoading: isLoadingCertificates,
    refetch: refetchCertificates,
  } = useQuery({
    queryKey: ['crew', crewMember?.id ?? '', 'certificates'],
    queryFn: () => getCrewCertificates(crewMember?.id ?? ''),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: crewMember != null,
    select: certs =>
      [...certs].sort(
        (a, b) =>
          (a.status === CrewMemberCertificateStatus.Rejected ? 1 : 0) -
            (b.status === CrewMemberCertificateStatus.Rejected ? 1 : 0) ||
          b.endDate.localeCompare(a.endDate),
      ),
  });

  useQueryErrorNotifier([error, errorCertficates]);

  const isLoadingCrewMemberData = isFetchingCrewMember;

  const queryClient = useQueryClient();

  const contextValue = useMemo<CrewTemplateContext>(
    () => ({
      crewMemberId,
      crewMember,
      setDisplayedCrewMemberFullName: setDisplayedCrewName,
      updateContextCrewMember: updatedCrew =>
        queryClient.setQueryData<CrewMember>(
          ['crew', crewMemberId ?? ''],
          updatedCrew,
        ),
      isFetching: isLoadingCrewMemberData,
      error,
      refetch: () => void refetch(),
      certificatesManagement: {
        crewMemberCertificates,
        isFetching: isFetchingCertificates,
        refetch: () => void refetchCertificates(),
      },
    }),
    [
      crewMemberId,
      crewMember,
      crewMemberCertificates,
      isLoadingCrewMemberData,
      error,
      isFetchingCertificates,
      queryClient,
      refetchCertificates,
      refetch,
    ],
  );

  const sidePanelActions = useMemo<SideActionItem[]>(() => {
    return [
      {
        id: 'information',
        label: t('content.information'),
        statusIcon: crewMember ? StatusIcon.Completed : StatusIcon.Pending,
        url: `/admin/crew/${crewMemberId ?? 'create'}`,
        isExactUrl: true,
      },
      {
        id: 'certifications',
        label:
          crewMember?.crewMemberRole === CrewMemberRole.Skipper
            ? t('content.certifications')
            : t('content.certifications-optional'),
        statusIcon: getIconCertificateStatus(
          !crewMember || isLoadingCertificates,
          crewMemberCertificates,
          crewMember?.crewMemberRole === CrewMemberRole.Crew,
        ),
        disabled: !crewMember && t('validation.certifications-tooltip'),
        url: crewMemberId
          ? `/admin/crew/${crewMemberId}/certifications`
          : undefined,
      },
    ];
  }, [
    t,
    crewMember,
    crewMemberId,
    crewMemberCertificates,
    isLoadingCertificates,
  ]);

  const [displayingDeletionDialog, setDisplayingDeletionDialog] =
    useState(false);

  return (
    <LeftPanelSectionTemplate
      title={
        crewMember ? (
          <span className="inline-flex flex-wrap items-center gap-2 md:items-end md:gap-6">
            <span>{`${crewMember.firstName} ${crewMember.lastName}`}</span>
            <VesselOrCrewStatusBadge
              entity="crew-member"
              status={crewMember.status}
              small={!isMdUp}
            />
          </span>
        ) : (
          displayedCrewName.trim() || t('content.untitled')
        )
      }
      isLoading={isFetchingCrewMember || isFetchingCertificates}
      actions={
        crewMember && (
          <>
            <CrewTemplateActions
              isAdmin={isAdmin}
              isLoading={isLoadingCrewMemberData}
              onDeleteRequest={() => setDisplayingDeletionDialog(true)}
            />
            <CrewDeletionDialog
              crewMemberId={displayingDeletionDialog ? crewMember.id : null}
              onClose={() => setDisplayingDeletionDialog(false)}
              onConfirm={() => {
                setDisplayingDeletionDialog(false);
                void disableNavigationGuard(() => navigate('/admin/crew'));
              }}
            />
          </>
        )
      }
      leftPanelActions={sidePanelActions}
      goBackLinkText={t('content.back-to-crew')}
      goBackLinkUrl="/admin/crew"
    >
      <Outlet context={contextValue} />
    </LeftPanelSectionTemplate>
  );
};

export default CrewTemplate;

export const CrewSubPageWrapper = () => (
  <OutletContextReliantRoute<CrewTemplateContext>
    getOutletContextState={({ crewMember, crewMemberId, isFetching }) =>
      // If the crew member has been deleted, it should not be accessible
      crewMember && !crewMember.deleteDate
        ? 'ready'
        : crewMemberId && isFetching
          ? 'pending'
          : 'error'
    }
    errorRedirectionPath="/admin/crew"
  />
);
